import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { ProjectService } from '../../../services/project.service';
import { CommonServices } from '../../../services/common.services';
import { NonNegativeDirective } from '../../../directives/non-negative-directive';
import { Observable, of } from 'rxjs';
import { ProjectVM } from '../../../models/view-models/vm-projects.model';
import { dm_project } from '../../../models/data-models/dm_project.model';
import { ApiService } from '../../../services/api.service';

@Component({
  selector: 'app-create-project-step-2',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    ReactiveFormsModule,
    CommonModule,
    MatChipsModule,
    NonNegativeDirective,
  ],
  templateUrl: './create-project-step-2.component.html',
  styleUrl: './create-project-step-2.component.css',
})
export class CreateObjectiveStep2Component implements OnInit {
  step2Form: FormGroup;
  isEditingTitle: boolean = false;
  projectTypeChip = '';
  isEditMode = false;
  startDateDisable: Date = new Date();
  initialStartDate: Date | null = null;
  editData: ProjectVM | null = null;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private projectSrvc: ProjectService,
    private commonService: CommonServices,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.step2Form = this.fb.group({
      purpose: [
        '',
        [Validators.required, Validators.minLength(1), this.commonService.noWhitespaceValidator()],
        [this.duplicateProjectValidator()],
      ],
      description: ['', [Validators.required, Validators.minLength(1), this.commonService.noWhitespaceValidator()]],
      projectCostTarget: ['', [Validators.required, Validators.min(0.001)]],
      startDate: ['', [Validators.required, this.startDateValidator.bind(this)]],
      endDate: [''],
    });

    this.loadExistingData();
  }

  private toStartOfDay(value: Date): Date {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  dateFilter = (d: Date | null): boolean => {
    if (!d) return false;

    const selectedDay = this.toStartOfDay(d);
    const today = this.toStartOfDay(new Date());

    if (selectedDay >= today) return true;

    if (this.initialStartDate) {
      const initialDay = this.toStartOfDay(this.initialStartDate);
      if (selectedDay.getTime() === initialDay.getTime()) return true;
    }

    return false;
  };

  startDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const selectedDay = this.toStartOfDay(new Date(control.value));
    const today = this.toStartOfDay(new Date());

    if (selectedDay >= today) return null;

    if (this.initialStartDate) {
      const initialDay = this.toStartOfDay(this.initialStartDate);
      if (selectedDay.getTime() === initialDay.getTime()) return null;
    }

    return { min: true };
  }

  getFullData() {
    return this.step2Form.value; // or step2Form.value
  }

  loadDataForEdit(): void {
    this.isEditMode = true;
    // this.loadExistingData();
  }

  private loadExistingData(): void {
    const existingData = this.projectSrvc.getProjectFormData().step2; // Fix the syntax error
    this.editData = this.projectSrvc.getCurrentProject();
    if (existingData && this.step2Form) {
      this.initialStartDate = existingData.startDate ? new Date(existingData.startDate) : new Date();
      this.step2Form.patchValue({
        purpose: existingData.purpose || existingData.title || '',
        description: existingData.description || existingData.details || '',
        projectCostTarget: existingData.projectCostTarget || '',
        startDate: existingData.startDate ? new Date(existingData.startDate) : new Date(),
        endDate: existingData.endDate ? new Date(existingData.endDate) : null,
      });

      this.startDateDisable = new Date();
    }
  }

  clearEndDate() {
    this.step2Form.patchValue({ endDate: '' });
  }

  private duplicateProjectValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || control.value.trim() === '') {
        return of(null);
      }

      const trimmedTitle = control.value.trim().toLowerCase();
      const currentProjectId = this.editData?.id_project ?? null;

      // Check if a project with the same title exists (case-insensitive)
      const isDuplicate = this.apiService.projects.some(
        (prj: dm_project) => prj.projectTitle.trim().toLowerCase() === trimmedTitle && prj.id !== currentProjectId
      );

      return of(isDuplicate ? { duplicateProject: true } : null);
    };
  }
}
